import * as R from 'ramda';

import api from '../../api';
import {
  backToFront,
  checkValue,
  createInheritableValue,
  frontToBack,
  localizedValues,
  localizedValuesToClearIfInherited,
  productsVariations,
  structureSelectOptions,
} from '../../services/helpers/dataStructuring';

const handleGetOptions = (
  setLoading,
  customerId,
  id,
  isCancelled,
  setSelectOptions,
  selectOptions,
  setSubProductVariations,
  setCatalog,
  hasParent,
) => {
  let subscriptionOptions = null;

  const promiseArray = [
    api.getSellingStoreOptions(customerId),
    api.getRenewingProductsByCustomerId({ customerId }),
    api.getFulfillmentTemplateByCustomerId(customerId),
    api.getCatalogsByCustomerId(customerId),
    api.getPriceFunctionsCustomerByIds(customerId),
    id && !hasParent ? api.getSubProductsById(id) : null,
  ];

  api
    .getPublicCustomerById(customerId)
    .then(({ data: curCustomer }) => {
      if (!curCustomer?.subscriptions || R.isEmpty(curCustomer?.subscriptions)) {
        promiseArray.push(api.getSubscriptionModelsByCustomerId(customerId));
      } else {
        subscriptionOptions = Object.keys(curCustomer?.subscriptions).map((item) => ({
          id: item,
          value: item,
        }));
      }

      Promise.all(promiseArray).then(
        ([
          sellingStores,
          renewingProductsOptions,
          fulfillmentTemplates,
          catalogs,
          priceFunctionsOptions,
          subProducts,
          subscriptions,
        ]) => {
          if (!subscriptionOptions) {
            subscriptionOptions = structureSelectOptions({
              options: subscriptions?.data?.items,
              optionValue: 'name',
            });
          }

          setSubProductVariations({
            bundledProducts: id === null ? [] : subProducts?.data?.items,
            variations: productsVariations(renewingProductsOptions?.data?.items, id),
          });

          const structureRenewingProducts =
            structureSelectOptions({
              options: renewingProductsOptions?.data?.items,
              optionValue: 'genericName',
              adIddToValue: true,
            }) || [];

          const renewingProducts = structureRenewingProducts.filter((it) => it.id !== id);

          if (!isCancelled) {
            setSelectOptions({
              ...selectOptions,
              sellingStores:
                structureSelectOptions({
                  options: sellingStores?.data?.items,
                  optionValue: 'name',
                  otherOptions: ['hostnames', 'saleLocales', 'defaultLocale'],
                }) || [],
              renewingProducts,
              fulfillmentTemplates:
                structureSelectOptions({
                  options: fulfillmentTemplates?.data?.items,
                  optionValue: 'name',
                }) || [],
              catalogs:
                structureSelectOptions({ options: catalogs?.data?.items, optionValue: 'name' }) ||
                [],
              priceFunctions:
                structureSelectOptions({
                  options: priceFunctionsOptions?.data?.items,
                  optionValue: 'name',
                }) || [],
              subscriptionModels: subscriptionOptions || [],
            });

            if (catalogs.data?.items?.length) {
              setCatalog(catalogs.data?.items[0].id);
            }
          }
        },
      );
    })
    .finally(() => {
      if (!id) {
        setLoading(false);
      }
    });
};

const handleGetProductDetails = (descriptionId, setVariablesDescriptions, setProductDetails) => {
  if (!descriptionId) return;

  const result = {};

  if (descriptionId?.state) {
    Promise.all([
      api.getProductDescriptionById(descriptionId?.value),
      api.getProductDescriptionById(descriptionId?.parentValue),
    ]).then(([productDescr, parentDescr]) => {
      const avail = [];

      localizedValues.forEach((it) => {
        if (productDescr?.data[it]) {
          Object.keys(productDescr?.data[it]).forEach((loc) => {
            if (avail.indexOf(loc) < 0) {
              avail.push(loc);
            }
          });
        }

        if (parentDescr?.data[it]) {
          Object.keys(parentDescr?.data[it]).forEach((loc) => {
            if (avail.indexOf(loc) < 0) {
              avail.push(loc);
            }
          });
        }
      });

      const { data } = productDescr;
      const { data: dataParent } = parentDescr;

      const i18nFields = avail.reduce((accumulator, current) => {
        const childLocalizedValues = localizedValues.reduce(
          (acc, curr) => ({ ...acc, [curr]: data[curr]?.[current] }),
          {},
        );

        const parentLocalizedValues = localizedValues.reduce(
          (acc, curr) => ({
            ...acc,
            [curr]: dataParent[curr] ? dataParent[curr][current] : '',
          }),
          {},
        );

        return {
          ...accumulator,
          [current]: backToFront(parentLocalizedValues, childLocalizedValues),
        };
      }, {});

      const productDescrData = { ...productDescr?.data };
      localizedValues.forEach((item) => delete productDescrData[item]);

      productDescrData.i18nFields = i18nFields;

      if (dataParent?.variableDescriptions) {
        setVariablesDescriptions(dataParent?.variableDescriptions);
      }

      setProductDetails(productDescrData);

      result.avail = avail;
    });

    return;
  }

  api.getProductDescriptionById(descriptionId).then(({ data }) => {
    const avail = [];

    localizedValues.forEach((it) => {
      if (data[it]) {
        Object.keys(data[it]).forEach((loc) => {
          if (avail.indexOf(loc) < 0) {
            avail.push(loc);
          }
        });
      }
    });

    const i18nFields = avail.reduce((accumulator, current) => {
      const childLocalizedValues = localizedValues.reduce(
        (acc, curr) => ({ ...acc, [curr]: data[curr] ? data[curr][current] : '' }),
        {},
      );

      return {
        ...accumulator,
        [current]: childLocalizedValues,
      };
    }, {});

    const productDescrData = { ...data };
    localizedValues.forEach((item) => delete productDescrData[item]);

    productDescrData.i18nFields = i18nFields;

    if (data?.variableDescriptions) {
      setVariablesDescriptions(data?.variableDescriptions);
    }

    setProductDetails(productDescrData);
  });
};

const handleEditorParsing = (data, parentData, setCurLocalizedData, setLocalizedData) => {
  const availableLocales = [];

  // Get an array of available locales from child and parent data
  localizedValues.forEach((it) => {
    if (data[it]) {
      Object.keys(data[it]).forEach((loc) => {
        if (availableLocales.indexOf(loc) < 0) {
          availableLocales.unshift(loc);
        }
      });
    }

    if (parentData && parentData[it]) {
      Object.keys(parentData[it]).forEach((loc) => {
        if (availableLocales.indexOf(loc) < 0) {
          availableLocales.push(loc);
        }
      });
    }
  });

  // Get a value of every localization field for every available locales
  // Transform data from structure
  // { someDescrition: {"en-UK" : "description value" } }
  // to the structure
  //  {"en-UK" : { someDescrition: "description value" } }
  const i18nFields = availableLocales.reverse().reduce((localesMap, locale) => {
    const childLocalizedValues = localizedValues.reduce((descriptionsMap, descriptionKey) => {
      const shouldSetEmptyValue =
        data.id === parentData?.id &&
        localizedValuesToClearIfInherited.indexOf(descriptionKey) > -1;

      return {
        ...descriptionsMap,
        [descriptionKey]:
          data[descriptionKey]?.[locale] && !shouldSetEmptyValue
            ? data[descriptionKey][locale]
            : '',
      };
    }, {});

    if (parentData) {
      const parentLocalizedValues = localizedValues.reduce(
        (descriptionsMap, descriptionKey) => ({
          ...descriptionsMap,
          [descriptionKey]: parentData[descriptionKey] ? parentData[descriptionKey][locale] : '',
        }),
        {},
      );

      return {
        ...localesMap,
        [locale]: backToFront(parentLocalizedValues, childLocalizedValues),
      };
    }

    return {
      ...localesMap,
      [locale]: childLocalizedValues,
    };
  }, {});

  const productDescrData = { ...data };

  localizedValues.forEach((item) => delete productDescrData[item]);

  productDescrData.i18nFields = { ...i18nFields };

  if (parentData) {
    const inheritedFallbackLocale = createInheritableValue(
      data.fallbackLocale,
      parentData.fallbackLocale,
    );

    productDescrData.fallbackLocale = inheritedFallbackLocale;
  } else if (!productDescrData?.fallbackLocale) {
    productDescrData.fallbackLocale = 'en-US';
  }

  if (setLocalizedData) {
    setLocalizedData({ ...productDescrData });
  }

  setCurLocalizedData({ ...productDescrData });
};

const frontToBackLocalizationDetails = (i18nFields) =>
  Object.entries(i18nFields).reduce((acc, [key, value]) => {
    if (value?.state !== 'inherits') {
      acc[key] = value?.value || value;
    }

    return acc;
  }, {});

const saveLocalizationDetails = (tempData, currentProductData, nxState) => {
  const frontToBackObj = frontToBack({
    ...tempData,
    fallbackLocale: checkValue(tempData?.fallbackLocale) || 'en-US',
    i18nFields: tempData?.i18nFields,
  });

  const i18nFields = Object.entries(frontToBackObj.i18nFields).reduce(
    (accumulator, [key, value]) => ({
      ...accumulator,
      [key]: frontToBackLocalizationDetails(value || {}),
    }),
    {},
  );

  const localizedValuesToSave = localizedValues.reduce((acc, cur) => {
    const localizedValue = Object.entries(i18nFields).reduce((ac, [key, value]) => {
      if (i18nFields[key][cur] !== undefined && i18nFields[key][cur]) {
        return { ...ac, [key]: value[cur] };
      }

      return ac;
    }, {});

    return {
      ...acc,
      [cur]: localizedValue || {},
    };
  }, {});

  if (frontToBackObj.i18nFields) {
    delete frontToBackObj.i18nFields;
  }

  const dataToSave = R.mergeRight(frontToBackObj, localizedValuesToSave);

  if (!frontToBackObj?.customerId) {
    frontToBackObj.customerId = currentProductData?.customerId?.state
      ? currentProductData?.customerId?.value
      : currentProductData?.customerId || nxState?.selectedCustomer?.id;
  }

  return dataToSave;
};

const notShowMaxPaymentsPart = ['PERMANENT', '7DAY'];

const beforeSend = (data, isPricesChanged) => {
  const res = { ...data };

  if (data.status.state) {
    res.status = {
      parentValue: data.status.parentValue,
      state: 'overrides',
      value: data.status.value,
    };
  }

  if (res?.subscriptionTemplate?.state === 'inherits') {
    delete res.subscriptionTemplate;
  }

  if (res?.fulfillmentTemplate?.state === 'inherits') {
    delete res.fulfillmentTemplate;
  }

  return res;
};

const defLocalizationObj = {
  localizedLongDesc: '',
  localizedManualRenewalEmailDesc: '',
  localizedMarketingName: '',
  localizedPurchaseEmailDesc: '',
  localizedShortDesc: '',
  localizedThankYouDesc: '',
};

const defProductVariationObj = {
  localizedLongDesc: { parentValue: '', state: 'inherits', value: '' },
  localizedManualRenewalEmailDesc: { parentValue: '', state: 'inherits', value: '' },
  localizedMarketingName: { parentValue: '', state: 'inherits', value: '' },
  localizedPurchaseEmailDesc: { parentValue: '', state: 'inherits', value: '' },
  localizedShortDesc: { parentValue: '', state: 'inherits', value: '' },
  localizedThankYouDesc: { parentValue: '', state: 'inherits', value: '' },
};

const tabLabels = [
  'general',
  'fulfillment',
  'subscription',
  'localizedContent',
  'prices',
  'productFiles',
  'productVariations',
];

const tabLabelsVariation = [
  'general',
  'fulfillment',
  'subscription',
  'localizedContent',
  'prices',
  'productFiles',
];

const defaultResourcesFiles = { key: 0, label: null, url: null };

const scrollTo = (element) => {
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const defaultSelectOptions = {
  sellingStores: null,
  renewingProducts: null,
  subscriptionModels: null,
  fulfillmentTemplates: null,
  catalogs: null,
  priceFunctions: null,
};

export {
  handleGetOptions,
  handleGetProductDetails,
  handleEditorParsing,
  saveLocalizationDetails,
  beforeSend,
  defLocalizationObj,
  defProductVariationObj,
  tabLabels,
  tabLabelsVariation,
  defaultResourcesFiles,
  notShowMaxPaymentsPart,
  scrollTo,
  defaultSelectOptions,
};
